import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ImageViewer.css';

interface ImageViewerProps {
  imageUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

const ImageViewer: React.FC<ImageViewerProps> = ({ imageUrl, isOpen, onClose }) => {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [touchStartDistance, setTouchStartDistance] = useState<number | null>(null);
  const [touchStartScale, setTouchStartScale] = useState(1);
  const [isPinching, setIsPinching] = useState(false);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const MIN_SCALE = 0.5;
  const MAX_SCALE = 5;
  const ZOOM_STEP = 0.25;

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + ZOOM_STEP, MAX_SCALE));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - ZOOM_STEP, MIN_SCALE));
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Reset zoom and position when image changes or viewer closes
  useEffect(() => {
    if (!isOpen) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
      setIsPinching(false);
      setIsDragging(false);
      setTouchStartDistance(null);
    }
  }, [isOpen, imageUrl]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      } else if (e.key === '0') {
        handleResetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, handleZoomIn, handleZoomOut, handleResetZoom]);

  // Handle mouse wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
    setScale((prev) => {
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, prev + delta));
      return newScale;
    });
  }, []);

  // Handle mouse drag for panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  }, [scale, position]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isDragging, dragStart, scale]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Handle click outside to close
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Calculate distance between two touch points
  const getTouchDistance = (touch1: React.Touch, touch2: React.Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Handle touch start for pinch-to-zoom
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation(); // Prevent event from bubbling to parent
    if (e.touches.length === 2) {
      // Pinch gesture
      e.preventDefault();
      setIsPinching(true);
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      setTouchStartDistance(distance);
      setTouchStartScale(scale);
    } else if (e.touches.length === 1 && scale > 1) {
      // Single touch pan
      e.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y
      });
    }
  }, [scale, position]);

  // Handle touch move for pinch-to-zoom and panning
  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.stopPropagation(); // Prevent event from bubbling to parent
    if (e.touches.length === 2 && isPinching && touchStartDistance !== null) {
      // Pinch-to-zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches[0], e.touches[1]);
      const scaleChange = distance / touchStartDistance;
      const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, touchStartScale * scaleChange));
      setScale(newScale);
    } else if (e.touches.length === 1 && isDragging && scale > 1) {
      // Single touch pan
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  }, [isPinching, touchStartDistance, touchStartScale, isDragging, dragStart, scale]);

  // Handle touch end
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.stopPropagation(); // Prevent event from bubbling to parent
    setIsPinching(false);
    setIsDragging(false);
    setTouchStartDistance(null);
  }, []);

  if (!isOpen) return null;

  return (
    <div
      className="image-viewer-overlay"
      onClick={handleOverlayClick}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div
        className="image-viewer-content"
        ref={containerRef}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button
          className="image-viewer-close"
          onClick={onClose}
          title="Close (Esc)"
        >
          ×
        </button>

        <div className="image-viewer-controls">
          <button
            className="image-viewer-control-btn"
            onClick={handleZoomOut}
            disabled={scale <= MIN_SCALE}
            title="Zoom Out (-)"
          >
            −
          </button>
          <span className="image-viewer-zoom-level">
            {Math.round(scale * 100)}%
          </span>
          <button
            className="image-viewer-control-btn"
            onClick={handleZoomIn}
            disabled={scale >= MAX_SCALE}
            title="Zoom In (+)"
          >
            +
          </button>
          <button
            className="image-viewer-control-btn"
            onClick={handleResetZoom}
            disabled={scale === 1}
            title="Reset Zoom (0)"
          >
            ⟲
          </button>
        </div>

        <img
          ref={imageRef}
          src={imageUrl}
          alt="Full size"
          className="image-viewer-img"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
            cursor: scale > 1 ? (isDragging || isPinching ? 'grabbing' : 'grab') : 'default',
            transition: (isDragging || isPinching) ? 'none' : 'transform 0.2s ease',
            touchAction: 'none' // Prevent default touch behaviors
          }}
          onMouseDown={handleMouseDown}
          draggable={false}
        />
      </div>
    </div>
  );
};

export default ImageViewer;

